import CircularProgress from '@mui/material/CircularProgress';
import React from 'react';
import { observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import { GOOGLE_TTS_MODEL_OPTIONS } from './types';
import GoogleAiVoiceHeader from './components/GoogleAiVoiceHeader';
import GoogleAiVoiceUnavailable from './components/GoogleAiVoiceUnavailable';
import GoogleOutputPanel from './components/GoogleOutputPanel';
import GoogleScriptEditorPanel from './components/GoogleScriptEditorPanel';
import { googleAiVoiceStyles } from './GoogleAiVoiceStyle';
import useGoogleAiVoiceController from './hooks/useGoogleAiVoiceController';

interface IGoogleAiVoiceProps {
  onSelect?: (aSrc: string, aFileName?: string) => void;
  onLoadingChange?: (loading: boolean) => void;
}

export default observer(function GoogleAiVoice({ onSelect, onLoadingChange }: IGoogleAiVoiceProps) {
  const theme = useTheme();
  const {
    addSegment,
    audioDuration,
    audioUrl,
    deleteSegment,
    draft,
    draftError,
    error,
    handleGenerateVoice,
    handleModelChange,
    handleUploadVoice,
    hasTextToGenerate,
    languageOptions,
    loading,
    loadingDraft,
    retrying,
    selectedLanguage,
    selectedVoiceA,
    selectedVoiceB,
    setLanguageCode,
    setMultiSpeaker,
    setPrompt,
    setPromptSpeakerA,
    setPromptSpeakerB,
    setSpeakerA,
    setSpeakerB,
    singleSpeakerText,
    setSingleSpeakerText,
    status,
    updateSegment,
    uploading,
    validationError,
    voiceError,
    voiceOptions,
    refreshCatalog,
  } = useGoogleAiVoiceController({ onSelect, onLoadingChange });

  if (loadingDraft || status === 'loading') {
    return (
      <div style={googleAiVoiceStyles.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  if (status === 'unavailable') {
    return <GoogleAiVoiceUnavailable message={voiceError ?? 'AI Voice is unavailable right now.'} onRetry={() => void refreshCatalog()} retrying={retrying} />;
  }

  return (
    <div style={googleAiVoiceStyles.root(theme)}>
      <GoogleAiVoiceHeader
        multiSpeaker={Boolean(draft.multiSpeaker)}
        prompt={draft.prompt ?? ''}
        promptSpeakerA={draft.promptSpeakerA ?? ''}
        promptSpeakerB={draft.promptSpeakerB ?? ''}
        onPromptChange={setPrompt}
        onPromptSpeakerAChange={setPromptSpeakerA}
        onPromptSpeakerBChange={setPromptSpeakerB}
      />

      <div style={googleAiVoiceStyles.contentRow(theme)}>
        <GoogleScriptEditorPanel
          multiSpeaker={Boolean(draft.multiSpeaker)}
          segments={draft.segments}
          singleSpeakerText={singleSpeakerText}
          onSingleSpeakerTextChange={setSingleSpeakerText}
          onUpdateSegment={updateSegment}
          onDeleteSegment={deleteSegment}
          onAddSegment={addSegment}
          audioUrl={audioUrl}
          audioDuration={audioDuration}
          disabled={status !== 'ready'}
        />

        <GoogleOutputPanel
          modelOptions={GOOGLE_TTS_MODEL_OPTIONS}
          selectedModel={draft.model}
          onModelChange={handleModelChange}
          languageOptions={languageOptions}
          selectedLanguage={selectedLanguage}
          onLanguageChange={setLanguageCode}
          multiSpeaker={Boolean(draft.multiSpeaker)}
          onMultiSpeakerChange={setMultiSpeaker}
          voiceOptions={voiceOptions}
          selectedVoiceA={selectedVoiceA}
          selectedVoiceB={selectedVoiceB}
          onVoiceAChange={(voice) => setSpeakerA({ voiceId: voice.voiceId, displayName: voice.displayName })}
          onVoiceBChange={(voice) => setSpeakerB({ voiceId: voice.voiceId, displayName: voice.displayName })}
          onGenerate={handleGenerateVoice}
          onApply={handleUploadVoice}
          canGenerate={hasTextToGenerate && voiceOptions.length > 0 && Boolean(selectedLanguage) && !validationError}
          canApply={audioUrl.length > 0}
          loading={loading}
          applying={uploading}
          disabled={status !== 'ready'}
          draftError={draftError}
          error={error}
          validationError={validationError}
          voiceError={voiceError}
        />
      </div>
    </div>
  );
});
